import { NavigationContext } from "components/file-viewer-footer";
import { BillEntriesNavigationState } from "recoils/bill-entries-navigation";
import { DocumentUploadNavigationState } from "recoils/document-upload-navigation";

export const buildNavigationContext = (
  billId: string,
  activeTab: "document" | "entry",
  billEntriesNav: BillEntriesNavigationState | null,
  docUploadNav: DocumentUploadNavigationState | null,
  fetchNextBillEntriesPage?: () => Promise<void>,
  fetchNextDocUploadPage?: () => Promise<void>,
  fetchPrevBillEntriesPage?: () => Promise<void>,
  fetchPrevDocUploadPage?: () => Promise<void>,
): NavigationContext | undefined => {
  if (billEntriesNav) {
    if (activeTab === "document") return undefined;
    const localIndex = Math.max(0, billEntriesNav.ids.indexOf(billId));
    const pageOffset = billEntriesNav.fetchContext?.pageOffset ?? 0;
    return {
      mode: "entry",
      ids: billEntriesNav.ids,
      currentIndex: localIndex,
      ...(billEntriesNav.fetchContext ? { displayIndex: localIndex + pageOffset } : {}),
      totalElements: billEntriesNav.totalCount,
      fetchNextPage: fetchNextBillEntriesPage,
      fetchPrevPage: pageOffset > 0 ? fetchPrevBillEntriesPage : undefined,
    };
  }

  if (docUploadNav) {
    const isAllDataEntriesTab = activeTab === "document";
    const docIndex = docUploadNav.entries.findIndex(
      (e) => e.navId === billId || e.billIds.includes(billId),
    );
    const docEntry = docIndex >= 0 ? docUploadNav.entries[docIndex] : null;
    const docNavIds = docUploadNav.entries.map((e) => e.navId);
    const pageOffset = docUploadNav.fetchContext?.pageOffset ?? 0;
    const localDocIndex = Math.max(0, docIndex);

    if (isAllDataEntriesTab) {
      return {
        mode: "document",
        ids: docNavIds,
        currentIndex: localDocIndex,
        ...(docUploadNav.fetchContext ? { displayIndex: localDocIndex + pageOffset } : {}),
        totalElements: docUploadNav.totalCount,
        fetchNextPage: fetchNextDocUploadPage,
        fetchPrevPage: pageOffset > 0 ? fetchPrevDocUploadPage : undefined,
      };
    }

    const billIds = docEntry?.billIds ?? [];
    const entryIndex = Math.max(0, billIds.indexOf(billId));
    const isLastEntry = entryIndex === billIds.length - 1;
    const isFirstEntry = entryIndex === 0;
    const isSingleEntry = billIds.length === 1;
    const hasNextDocLoaded = docIndex >= 0 && docIndex < docUploadNav.entries.length - 1;
    const hasMoreDocPages = docUploadNav.entries.length < docUploadNav.totalCount;
    const isAtLastLoadedDoc = docIndex === docUploadNav.entries.length - 1;
    const canNavigateToNextDoc = hasNextDocLoaded || (isAtLastLoadedDoc && hasMoreDocPages);
    const hasPrevDoc = docIndex > 0;

    const prevDocEntry = hasPrevDoc ? docUploadNav.entries[docIndex - 1] : null;
    const prevDocTargetId = prevDocEntry?.billIds.length
      ? prevDocEntry.billIds[prevDocEntry.billIds.length - 1]
      : prevDocEntry?.navId;

    return {
      mode: "entry",
      ids: billIds,
      currentIndex: entryIndex,
      totalElements: billIds.length,
      ...(isSingleEntry
        ? { singleEntryDocInfo: { displayIndex: localDocIndex + pageOffset, totalElements: docUploadNav.totalCount } }
        : {}),
      // When the current doc is the first in the loaded array but prior server pages exist, expose a
      // backward-fetch so the nav controls can load the previous page and navigate to its last entry.
      ...(docIndex === 0 && pageOffset > 0 ? { fetchPrevPage: fetchPrevDocUploadPage } : {}),
      ...(isLastEntry && canNavigateToNextDoc
        ? {
            parentDocumentContext: {
              ids: docNavIds,
              currentIndex: docIndex,
              ...(docUploadNav.fetchContext ? { displayIndex: docIndex + pageOffset } : {}),
              totalElements: docUploadNav.totalCount,
              fetchNextPage: fetchNextDocUploadPage,
            },
          }
        : {}),
      ...(isFirstEntry && hasPrevDoc && prevDocTargetId
        ? { prevParentDocumentContext: { targetEntryId: prevDocTargetId } }
        : {}),
    };
  }

  return undefined;
};
